import React, { useState, useEffect } from "react";
import AsyncSelect from "react-select/async";
import { customFetch } from '../apiClient';
import ServiceReceipt from '../pages/ServiceReceipt';

const IPDServiceDetailsTable = () => {
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [serviceReceiptData, setServiceReceiptData] = useState(null);
  const [showServiceReceipt, setShowServiceReceipt] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);

  // Fetch company details
  useEffect(() => {
    const hospitalId = sessionStorage.getItem("hospitalId");
    if (hospitalId) {
     customFetch(`/Company/${hospitalId}`)
        .then((res) => {
          if (!res.ok) throw new Error("Failed to load company details");
          return res.json();
        })
        .then((data) => setCompanyDetails(data))
        .catch(console.error);
    }
  }, []);

  // Auto-dismiss alerts
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // ✅ UPDATED: Load patient options with Reg Date
  const loadPatientOptions = async (inputValue) => {
    if (!inputValue) return [];
    try {
  const res = await customFetch(`/IPDServiceDetailDT/ipd-with-services?search=${encodeURIComponent(inputValue)}`);
      if (!res.ok) return [];
      const data = await res.json();
      
      const uniquePatientsMap = new Map();
      data.forEach(item => {
        if (!uniquePatientsMap.has(item.ipD_ID)) {
          // Date formatting for dropdown (Using regDate fallback to ipD_Date if needed)
          const regDateFormatted = item.regDate || item.ipD_Date
            ? new Date(item.regDate || item.ipD_Date).toLocaleDateString('en-IN', { 
                day: '2-digit', month: 'short', year: 'numeric' 
              }) 
            : "N/A";

          uniquePatientsMap.set(item.ipD_ID, {
            label: `${item.name} (${item.mobileNo}) | UHID: ${item.uhiD_No} | IPD No: ${item.ipD_SRNO} | Reg Date: ${regDateFormatted}`,
            value: item.ipD_ID
          });
        }
      });
      return Array.from(uniquePatientsMap.values());
    } catch {
      return [];
    }
  };

  const handlePatientChange = async (selectedOption) => {
    setSelectedPatient(selectedOption);
    setError(null);
    setMessage(null);

    if (!selectedOption) {
      setServices([]);
      return;
    }

    setLoading(true);
    try {
     const res = await customFetch(`/IPDServiceDetailDT/by-ipd/${selectedOption.value}`);
      if (!res.ok) {
        setServices([]);
        setError("No service details found for selected patient.");
        setLoading(false);
        return;
      }
      const data = await res.json();
      setServices(data);
    } catch (e) {
      setError("Failed to load service details: " + e.message);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (service) => {
    setEditId(service.servSrNo);
    const servdateValue = service.servdate ? new Date(service.servdate).toISOString().slice(0, 16) : "";
    setEditData({
      qty: service.qty || 1,
      servRate: service.servRate || 0,
      amount: service.amount || 0,
      remarks: service.remarks || "",
      servdate: servdateValue,
    });
    setError(null);
    setMessage(null);
  };

  const handleChange = (field, value) => {
    setEditData((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === "qty" || field === "servRate") {
        const qtyNum = field === "qty" ? parseFloat(value) : parseFloat(prev.qty);
        const rateNum = field === "servRate" ? parseFloat(value) : parseFloat(prev.servRate);
        if (!isNaN(qtyNum) && !isNaN(rateNum)) {
          updated.amount = (qtyNum * rateNum).toFixed(2);
        } else {
          updated.amount = "";
        }
      }
      return updated;
    });
  };

  const saveService = async () => {
    if (!editId) return;

    const qtyNum = parseFloat(editData.qty);
    const rateNum = parseFloat(editData.servRate);

    if (qtyNum <= 0) {
      setError("Quantity must be greater than zero.");
      return;
    }
    if (rateNum < 0) {
      setError("Service rate cannot be negative.");
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const token = sessionStorage.getItem("token");
      const serviceToUpdateIndex = services.findIndex((s) => s.servSrNo === editId);
      if (serviceToUpdateIndex === -1) throw new Error("Service detail not found");

      const serviceToUpdate = services[serviceToUpdateIndex];

  const res = await customFetch(`/IPDServiceDetailDT/${editId}`, {
  method: "PUT",
  body: JSON.stringify({
    ...serviceToUpdate,
    qty: qtyNum,
    servRate: rateNum,
    amount: Number(editData.amount),
    remarks: editData.remarks,
    servdate: editData.servdate,
  }),
});

      if (res.status === 403) setError("You do not have permission to edit.");
      else if (!res.ok) {
        const text = await res.text();
        setError(text || "Failed to update service detail.");
      } else {
        setMessage("Service detail updated successfully.");

        setServices(prev => {
          const newServices = [...prev];
          newServices[serviceToUpdateIndex] = {
            ...newServices[serviceToUpdateIndex],
            qty: qtyNum,
            servRate: rateNum,
            amount: Number(editData.amount),
            remarks: editData.remarks,
            servdate: editData.servdate,
          };
          return newServices;
        });

        setEditId(null);
        setEditData({});
      }
    } catch (e) {
      setError("Network error: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleServicePreview = async (service) => {
    try {
     const res = await customFetch(`/IPDServiceDetailDT/receipt/${service.servSrNo}`);
      if (!res.ok) throw new Error("Failed to load service receipt data");
      const data = await res.json();
      setServiceReceiptData(data);
      setShowServiceReceipt(true);
    } catch (err) {
      setError("Failed to load service receipt");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((sid) => sid !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
    } else {
      const allIds = services.map((s) => s.servSrNo);
      setSelectedIds(allIds);
      setSelectAll(true);
    }
  };

  const deleteService = async (id = null) => {
    if (!id && selectedIds.length === 0) {
      setError("Please select at least one service detail to delete.");
      return;
    }

    const idsToDelete = selectedIds.length > 0 ? selectedIds : id ? [id] : [];
    if (idsToDelete.length === 0) return;

    if (!window.confirm(`Are you sure to delete ${idsToDelete.length > 1 ? "these" : "this"} service detail${idsToDelete.length > 1 ? "s" : ""}?`)) return;

    setLoading(true);
    setError(null);
    setMessage(null);

    let allDeletedSuccessfully = true;

    try {
      const token = sessionStorage.getItem("token");

      for (const delId of idsToDelete) {
     // token wali line hata dein
const res = await customFetch(`/IPDServiceDetailDT/${delId}`, {
  method: "DELETE",
});

        if (!res.ok) {
          allDeletedSuccessfully = false;
          const text = await res.text();
          setError(text || `Failed to delete service detail with ID ${delId}.`);
        }
      }

      if (allDeletedSuccessfully) {
        setServices((prev) => prev.filter((s) => !idsToDelete.includes(s.servSrNo)));
        setMessage(`${idsToDelete.length} service detail${idsToDelete.length > 1 ? "s" : ""} deleted successfully.`);
        setSelectedIds([]);
        setSelectAll(false);
        if (idsToDelete.includes(editId)) {
          setEditId(null);
          setEditData({});
        }
      }
    } catch (e) {
      setError("Network error: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const getTotalAmount = () => {
    return services.reduce((sum, srv) => sum + (srv.amount || 0), 0).toFixed(2);
  };

  // ✅ DYNAMIC STYLES FOR REACT SELECT (COMPACT)
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '30px', /* ✅ Compact Height */
      height: '30px',
      fontSize: '11px', /* ✅ Compact Font */
      borderRadius: '6px',
      borderColor: state.isFocused ? 'var(--theme-color, #0088cc)' : '#cbd5e0',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(0, 0, 0, 0.05)' : 'none',
      '&:hover': { borderColor: 'var(--theme-color, #0088cc)' }
    }),
    valueContainer: (base) => ({ ...base, padding: '0 6px' }),
    input: (base) => ({ ...base, margin: '0', padding: '0' }),
    indicatorsContainer: (base) => ({ ...base, height: '30px' }),
    dropdownIndicator: (base) => ({ ...base, padding: '4px' }),
    clearIndicator: (base) => ({ ...base, padding: '4px' }),
    option: (base, state) => ({
      ...base,
      fontSize: '11px',
      padding: '6px 10px',
      backgroundColor: state.isSelected 
        ? 'var(--theme-color, #0088cc)' 
        : state.isFocused 
          ? 'color-mix(in srgb, var(--theme-color, #0088cc), white 90%)' 
          : 'white',
      color: state.isSelected ? 'white' : '#333',
      cursor: 'pointer',
      '&:active': { backgroundColor: 'var(--theme-color, #0088cc)', color: 'white' }
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      fontSize: '11px',
      borderRadius: '6px'
    }),
    placeholder: (base) => ({ ...base, color: '#a0aec0', fontSize: '11px' })
  };

  return (
    <div className="service-table-container compact-ui">
      <div className="service-table-card">
        {/* Header */}
        <div className="service-table-header">
          <div className="header-left">
            <div className="header-icon">
              <i className="bi bi-pencil-square"></i>
            </div>
            <div className="header-text">
              <h5>Edit IPD Service Details</h5>
              <p>Manage and update service records for patients</p>
            </div>
          </div>
        </div>

        <div className="card-content">
          {/* Patient Search */}
          <div className="search-section">
            <label className="search-label">
              <i className="bi bi-person-search"></i>
              Search Patient
            </label>
            <AsyncSelect
              cacheOptions
              defaultOptions
              loadOptions={loadPatientOptions}
              onChange={handlePatientChange}
              placeholder="Search by Name, Mobile No or IPD SRNO..."
              isClearable
              styles={selectStyles}
            />
          </div>

          {/* Alerts */}
          {error && (
            <div className="alert-custom alert-danger slide-in">
              <i className="bi bi-exclamation-triangle-fill"></i>
              <span>{error}</span>
              <button className="alert-close" onClick={() => setError(null)}><i className="bi bi-x"></i></button>
            </div>
          )}
          {message && (
            <div className="alert-custom alert-success slide-in">
              <i className="bi bi-check-circle-fill"></i>
              <span>{message}</span>
              <button className="alert-close" onClick={() => setMessage(null)}><i className="bi bi-x"></i></button>
            </div>
          )}

          {loading && (
            <div className="loading-indicator">
              <span className="spinner-border spinner-border-sm me-2"></span>
              Loading...
            </div>
          )}

          {/* Services Table */}
          {selectedPatient && (
            <div className="table-section">
              <div className="table-header-info">
                <div className="table-title">
                  <i className="bi bi-list-check"></i>
                  <span>Service Records</span>
                  {services.length > 0 && (
                    <span className="badge-count">{services.length}</span>
                  )}
                </div>
                {services.length > 0 && (
                  <div className="total-amount">
                    <span>Total:</span>
                    <span className="amount-value">₹{getTotalAmount()}</span>
                  </div>
                )}
              </div>

              <div className="table-responsive">
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th className="checkbox-col text-center">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={toggleSelectAll}
                          title="Select All"
                        />
                      </th>
                      <th className="icon-col text-center">Preview</th>
                      <th>Service Name</th>
                      <th className="text-center">Service Date</th>
                      <th className="text-center">Qty</th>
                      <th className="text-center">Rate</th>
                      <th className="text-center">Amount</th>
                      <th>Remarks</th>
                      <th className="text-center" style={{ width: '80px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.length === 0 && !loading && (
                      <tr>
                        <td colSpan="9" className="empty-state">
                          <i className="bi bi-inbox"></i>
                          <p>No service details found</p>
                        </td>
                      </tr>
                    )}

                    {services.map((service) => (
                      <tr key={service.servSrNo}>
                        <td className="text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(service.servSrNo)}
                            onChange={() => toggleSelect(service.servSrNo)}
                          />
                        </td>
                        <td className="text-center">
                          <button
                            className="btn-icon btn-preview"
                            onClick={() => handleServicePreview(service)}
                            title="Preview Service Receipt"
                          >
                            <i className="bi bi-eye"></i>
                          </button>
                        </td>

                        <td className="fw-medium">{service.serviceName}</td>

                        <td>
                          {editId === service.servSrNo ? (
                            <input
                              type="datetime-local"
                              className="form-control-inline"
                              value={editData.servdate || ""}
                              onChange={(e) => handleChange("servdate", e.target.value)}
                            />
                          ) : (
                            <span className="text-center d-block date-text">
                              {service.servdate ? new Date(service.servdate).toLocaleString('en-IN', { 
                                day: '2-digit', 
                                month: '2-digit', 
                                year: 'numeric', 
                                hour: '2-digit', 
                                minute: '2-digit',
                                hour12: true 
                              }) : '-'}
                            </span>
                          )}
                        </td>

                        <td>
                          {editId === service.servSrNo ? (
                            <input
                              type="number"
                              className="form-control-inline text-center"
                              value={editData.qty || ""}
                              onChange={(e) => handleChange("qty", e.target.value)}
                              min="1"
                              step="0.1"
                            />
                          ) : (
                            <span className="text-center d-block">{service.qty}</span>
                          )}
                        </td>

                        <td>
                          {editId === service.servSrNo ? (
                            <input
                              type="number"
                              className="form-control-inline text-center"
                              value={editData.servRate || ""}
                              onChange={(e) => handleChange("servRate", e.target.value)}
                              min="0"
                              step="0.01"
                            />
                          ) : (
                            <span className="text-center d-block">₹{service.servRate}</span>
                          )}
                        </td>

                        <td>
                          {editId === service.servSrNo ? (
                            <input
                              type="number"
                              className="form-control-inline text-center"
                              value={editData.amount || ""}
                              readOnly
                            />
                          ) : (
                            <span className="text-center d-block fw-semibold text-success">₹{service.amount}</span>
                          )}
                        </td>

                        <td>
                          {editId === service.servSrNo ? (
                            <input
                              type="text"
                              className="form-control-inline"
                              value={editData.remarks || ""}
                              onChange={(e) => handleChange("remarks", e.target.value)}
                              placeholder="Comments..."
                            />
                          ) : (
                            <span className="text-muted small-text">{service.remarks || '-'}</span>
                          )}
                        </td>

                        <td className="text-center">
                          {editId === service.servSrNo ? (
                            <div className="btn-group-actions">
                              <button
                                className="btn-icon btn-save"
                                onClick={saveService}
                                disabled={loading}
                                title="Save"
                              >
                                <i className="bi bi-check-circle"></i>
                              </button>
                              <button
                                className="btn-icon btn-cancel"
                                onClick={() => {
                                  setEditId(null);
                                  setEditData({});
                                  setError(null);
                                  setMessage(null);
                                }}
                                disabled={loading}
                                title="Cancel"
                              >
                                <i className="bi bi-x-circle"></i>
                              </button>
                            </div>
                          ) : (
                            <div className="btn-group-actions">
                              <button
                                className="btn-icon btn-edit"
                                onClick={() => startEditing(service)}
                                title="Edit"
                              >
                                <i className="bi bi-pencil-square"></i>
                              </button>
                              <button
                                className="btn-icon btn-delete"
                                onClick={() => deleteService(service.servSrNo)}
                                title="Delete"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {selectedIds.length > 0 && (
                <div className="bulk-actions">
                  <span className="selected-count">
                    {selectedIds.length} item{selectedIds.length > 1 ? 's' : ''} selected
                  </span>
                  <button
                    className="btn-bulk-delete"
                    onClick={() => deleteService()}
                    disabled={loading}
                  >
                    <i className="bi bi-trash"></i>
                    Delete Selected
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Receipt Modal */}
      {companyDetails && showServiceReceipt && serviceReceiptData && (
        <ServiceReceipt
          show={showServiceReceipt}
          data={serviceReceiptData.services}
          patient={serviceReceiptData.patient}
          companyDetails={companyDetails}
          onClose={() => setShowServiceReceipt(false)}
          onPrint={() => window.print()}
        />
      )}

      {/* ✅ COMPACT UI STYLES ✅ */}
      <style jsx>{`
        .compact-ui {
          font-family: system-ui, -apple-system, sans-serif;
        }

        .service-table-container { padding: 0; }

        .service-table-card {
          background: white; border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
          border: 1px solid #e0e6ed; overflow: visible;
        }

        /* Animations */
        @keyframes slideIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        .slide-in { animation: slideIn 0.2s ease; }

        /* Header */
        .service-table-header {
          background: var(--theme-color, #0088cc);
          padding: 10px 16px; border-bottom: 2px solid rgba(0,0,0,0.1);
          border-top-left-radius: 8px; border-top-right-radius: 8px;
        }

        .header-left { display: flex; align-items: center; gap: 10px; }

        .header-icon {
          width: 32px; height: 32px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 6px; display: flex; align-items: center; justify-content: center;
          font-size: 16px; color: white;
        }

        .header-text h5 { margin: 0; color: white; font-size: 15px; font-weight: 600; line-height: 1.1; }
        .header-text p { margin: 0; color: rgba(255, 255, 255, 0.85); font-size: 10px; }

        .card-content { padding: 12px 16px; }

        .search-section { margin-bottom: 12px; position: relative; z-index: 15; }

        .search-label {
          display: flex; align-items: center; gap: 4px; font-size: 11px;
          font-weight: 600; color: #2c3e50; margin-bottom: 4px;
        }
        .search-label i { color: var(--theme-color, #0088cc); font-size: 10px; }

        /* Alerts */
        .alert-custom {
          margin-bottom: 12px; padding: 8px 12px; border-radius: 6px;
          display: flex; align-items: center; gap: 8px;
          font-size: 11px; font-weight: 500; position: relative;
        }
        .alert-danger { background: #fee; color: #c33; border: 1px solid #fcc; }
        .alert-success { background: #d4edda; color: #155724; border: 1px solid #c3e6cb; }
        .alert-custom i { font-size: 12px; }
        .alert-close { position: absolute; right: 8px; background: transparent; border: none; cursor: pointer; opacity: 0.6; padding: 0;}
        .alert-close:hover { opacity: 1; }

        .loading-indicator {
          padding: 12px; text-align: center; color: var(--theme-color, #0088cc);
          font-size: 11px; font-weight: 500;
        }

        .table-section {
          background: white; border-radius: 8px; padding: 0; border: 1px solid #e0e6ed; overflow: hidden;
        }

        .table-header-info {
          display: flex; justify-content: space-between; align-items: center;
          padding: 10px 12px; border-bottom: 1px solid #e0e6ed; background: #f8f9fa;
        }

        .table-title { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; color: #2c3e50; }
        .table-title i { color: var(--theme-color, #0088cc); font-size: 14px; }

        .badge-count {
          background: var(--theme-color, #0088cc);
          color: white; padding: 2px 6px; border-radius: 10px; font-size: 10px;
        }

        .total-amount { display: flex; align-items: center; gap: 6px; font-size: 12px; }
        .total-amount span:first-child { color: #7f8c8d; }
        .amount-value { font-weight: 700; color: #27ae60; }

        .table-responsive { overflow-x: auto; }
        .custom-table { width: 100%; border-collapse: collapse; font-size: 11px; }

        .custom-table thead {
          background: var(--theme-color, #0088cc);
        }

        .custom-table thead th {
          color: white; font-weight: 600; padding: 6px 8px;
          font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; border: none;
        }

        .checkbox-col { width: 30px; text-align: center; }
        .icon-col { width: 40px; text-align: center; }

        .custom-table tbody tr { transition: all 0.2s; border-bottom: 1px solid #e0e6ed; }
        .custom-table tbody tr:hover { background: #f8f9fa; }
        .custom-table tbody td { padding: 6px 8px; vertical-align: middle; }

        /* Form Controls Inline for Table */
        .form-control-inline {
          width: 100%; padding: 4px 6px; border: 1px solid #cbd5e0; border-radius: 4px;
          font-size: 11px; height: 26px; transition: all 0.2s; outline: none;
        }
        .form-control-inline:focus { border-color: var(--theme-color, #0088cc); }

        .text-center { text-align: center; }
        .fw-medium { font-weight: 500; }
        .fw-semibold { font-weight: 600; }
        .text-success { color: #27ae60; }
        .text-muted { color: #7f8c8d; }
        .small-text { font-size: 10px; font-style: italic; }
        .date-text { font-size: 10px; color: #4b5563; }
        .d-block { display: block; }

        .empty-state { text-align: center; padding: 30px 20px; color: #7f8c8d; }
        .empty-state i { font-size: 32px; margin-bottom: 8px; display: block; opacity: 0.5; }
        .empty-state p { margin: 0; font-size: 11px; }

        /* Action Buttons */
        .btn-group-actions { display: flex; justify-content: center; gap: 4px; }
        
        .btn-icon {
          width: 24px; height: 24px; padding: 0; display: inline-flex; align-items: center; justify-content: center;
          border-radius: 4px; border: none; cursor: pointer; transition: all 0.2s; font-size: 11px;
        }

        .btn-preview { background: #e0f2fe; color: #0369a1; }
        .btn-preview:hover { background: #0369a1; color: white; transform: translateY(-1px); }

        .btn-edit { background: #dbeafe; color: #1e40af; }
        .btn-edit:hover { background: #1e40af; color: white; transform: translateY(-1px); }

        .btn-delete { background: #fee2e2; color: #dc2626; }
        .btn-delete:hover { background: #dc2626; color: white; transform: translateY(-1px); }

        .btn-save { background: #d1fae5; color: #065f46; }
        .btn-save:hover { background: #059669; color: white; transform: translateY(-1px); }

        .btn-cancel { background: #f3f4f6; color: #4b5563; }
        .btn-cancel:hover { background: #6b7280; color: white; transform: translateY(-1px); }

        .btn-icon:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

        /* Bulk Actions */
        .bulk-actions {
          padding: 10px 12px; border-top: 1px solid #e0e6ed; background: #f8f9fa;
          display: flex; justify-content: space-between; align-items: center;
        }

        .selected-count { font-size: 11px; font-weight: 500; color: #2c3e50; }

        .btn-bulk-delete {
          padding: 6px 12px; border-radius: 6px; border: none;
          background: #fee2e2; color: #dc2626; font-size: 11px; font-weight: 600; cursor: pointer;
          transition: all 0.2s ease; display: flex; align-items: center; gap: 4px;
        }
        .btn-bulk-delete:hover:not(:disabled) { background: #dc2626; color: white; transform: translateY(-1px); }
        .btn-bulk-delete:disabled { opacity: 0.6; cursor: not-allowed; }

        @media (max-width: 768px) {
          .table-header-info { flex-direction: column; gap: 8px; align-items: flex-start; }
          .bulk-actions { flex-direction: column; gap: 8px; align-items: stretch; }
          .btn-bulk-delete { width: 100%; justify-content: center; }
        }
      `}</style>
    </div>
  );
};

export default IPDServiceDetailsTable;